import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { NoticeLevel } from '@shared/designSystem/NoticeBanner'
import { isExternalHref } from '@shared/designSystem/NoticeBanner'
import {
    ContentLanguage,
    languageForCountryCode,
    resolveLocalizedContent,
} from '@shared/localization/languageForCountryCode'
import { PlatformNotice, PlatformNoticeUserState, noticeKey } from '@models/PlatformNotice'
import { PlatformNoticesRealtimeServiceInterface } from '@services/PlatformNoticesRealtimeService'
import { PlatformNoticesAnalyticsHelper } from '@core/Analytics/PlatformNoticesAnalyticsHelper'
import { PLATFORM_NOTICES_STRINGS, PlatformNoticesStrings } from './PlatformNoticesStrings'

export const MAX_BAR_ROWS = 2
const UPDATED_CUE_MS = 24 * 60 * 60 * 1000
/** Edits closer than this to publication are treated as part of publishing. */
const UPDATE_GRACE_MS = 60 * 1000
const MAX_TIMEOUT_MS = 2_147_483_647

const LEVEL_RANK: Record<NoticeLevel, number> = { critical: 0, warning: 1, communication: 2 }

// ---------------------------------------------------------------------------
// Pure selection logic (exported for tests)

export function isNoticeLive(notice: PlatformNotice, now: number): boolean {
    const started = notice.startsAt === undefined || notice.startsAt <= now
    const notEnded = notice.endsAt === undefined || notice.endsAt > now
    return started && notEnded
}

/** critical → warning → communication, newest first within a level. */
export function sortNotices(notices: PlatformNotice[]): PlatformNotice[] {
    return [...notices].sort((a, b) =>
        LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || b.publishedAt - a.publishedAt || a.id.localeCompare(b.id)
    )
}

/** Next instant at which the visible set or a cue changes on its own. */
export function nextBoundary(notices: PlatformNotice[], now: number): number | undefined {
    const candidates: number[] = []
    for (const notice of notices) {
        if (notice.startsAt !== undefined && notice.startsAt > now) candidates.push(notice.startsAt)
        if (notice.endsAt !== undefined && notice.endsAt > now) candidates.push(notice.endsAt)
        const cueEnd = notice.updatedAt + UPDATED_CUE_MS
        if (isEdited(notice) && cueEnd > now) candidates.push(cueEnd)
    }
    return candidates.length ? Math.min(...candidates) : undefined
}

export function isEdited(notice: PlatformNotice): boolean {
    return notice.updatedAt - notice.publishedAt > UPDATE_GRACE_MS
}

export function isDismissed(
    notice: PlatformNotice,
    states: Record<string, PlatformNoticeUserState>,
    localDismissed: Set<string>
): boolean {
    if (notice.level === 'critical') {
        return false
    }
    return states[notice.id]?.dismissedVersion === notice.version || localDismissed.has(noticeKey(notice))
}

export function isAcknowledged(
    notice: PlatformNotice,
    states: Record<string, PlatformNoticeUserState>,
    localAcknowledged: Set<string>
): boolean {
    return states[notice.id]?.acknowledgedVersion === notice.version || localAcknowledged.has(noticeKey(notice))
}

/**
 * Rows shown in the bar. Critical notices are always rows and never counted in
 * the pill; others need to have been "placed" (present at load, arrived while
 * the bar was empty, or promoted by a navigation). Everything else is counted.
 */
export function selectBarRows(
    visible: PlatformNotice[],
    placedIDs: Set<string>,
    maxRows: number = MAX_BAR_ROWS
): { rows: PlatformNotice[], hiddenCount: number } {
    const critical = visible.filter((notice) => notice.level === 'critical')
    const placed = visible.filter((notice) => notice.level !== 'critical' && placedIDs.has(notice.id))
    const rows = [...critical, ...placed].slice(0, Math.max(maxRows, critical.length))
    return { rows, hiddenCount: visible.length - rows.length }
}

/** Critical notices still waiting for "Entendido", newest first. */
export function pendingAcknowledgements(
    visible: PlatformNotice[],
    states: Record<string, PlatformNoticeUserState>,
    localAcknowledged: Set<string>
): PlatformNotice[] {
    return visible
        .filter((notice) => notice.level === 'critical' && notice.requiresAcknowledgement)
        .filter((notice) => !isAcknowledged(notice, states, localAcknowledged))
        .sort((a, b) => b.updatedAt - a.updatedAt)
}

export function linkHost(href: string): string {
    if (!isExternalHref(href)) {
        return 'internal'
    }
    try {
        return new URL(href).host
    } catch {
        return 'invalid'
    }
}

const DATE_LOCALE: Record<ContentLanguage, string> = { es: 'es-AR', pt: 'pt-BR', en: 'en-US' }

export function formatPublishedAt(timestamp: number, language: ContentLanguage): string {
    const date = new Date(timestamp)
    const day = date.toLocaleDateString(DATE_LOCALE[language], { day: '2-digit', month: '2-digit', year: 'numeric' })
    const time = date.toLocaleTimeString(DATE_LOCALE[language], { hour: '2-digit', minute: '2-digit', hour12: false })
    return `${day} ${time}`
}

// ---------------------------------------------------------------------------
// Local fallback for writes that could not reach RTDB (kept per browser)

type LocalNoticeState = { dismissed: string[], acknowledged: string[] }

const localStateKey = (uid: string) => `platformNotices.localState.${uid}`

function readLocalState(uid: string | undefined): LocalNoticeState {
    if (!uid) {
        return { dismissed: [], acknowledged: [] }
    }
    try {
        const parsed = JSON.parse(window.localStorage.getItem(localStateKey(uid)) ?? 'null')
        return {
            dismissed: Array.isArray(parsed?.dismissed) ? parsed.dismissed : [],
            acknowledged: Array.isArray(parsed?.acknowledged) ? parsed.acknowledged : [],
        }
    } catch {
        return { dismissed: [], acknowledged: [] }
    }
}

function persistLocalState(uid: string, kind: keyof LocalNoticeState, key: string) {
    try {
        const current = readLocalState(uid)
        if (!current[kind].includes(key)) {
            current[kind] = [...current[kind], key].slice(-200)
        }
        window.localStorage.setItem(localStateKey(uid), JSON.stringify(current))
    } catch {
        // Storage unavailable (private mode, blocked): the in-memory set still hides it this session.
    }
}

// ---------------------------------------------------------------------------
// View model

export interface PlatformNoticeDisplay {
    key: string
    notice: PlatformNotice
    level: NoticeLevel
    title: string
    body: string
    meta: string[]
    cta?: { label: string, href: string }
    canDismiss: boolean
}

export interface PlatformNoticesViewModelInput {
    service: PlatformNoticesRealtimeServiceInterface
    uid?: string
    organizationID?: string
    organizationName?: string
    countryCode?: string
    hasMultipleOrganizations: boolean
    /** Changes on every route change; deferred notices are promoted then. */
    navigationKey: string
    now?: () => number
}

export function usePlatformNoticesViewModel(input: PlatformNoticesViewModelInput) {
    const { service, uid, organizationID, organizationName, countryCode, hasMultipleOrganizations, navigationKey } = input
    const clock = input.now ?? Date.now

    const language = languageForCountryCode(countryCode)
    const strings: PlatformNoticesStrings = PLATFORM_NOTICES_STRINGS[language]

    const [notices, setNotices] = useState<PlatformNotice[]>([])
    const [userStates, setUserStates] = useState<Record<string, PlatformNoticeUserState>>({})
    const [localDismissed, setLocalDismissed] = useState<Set<string>>(() => new Set(readLocalState(uid).dismissed))
    const [localAcknowledged, setLocalAcknowledged] = useState<Set<string>>(() => new Set(readLocalState(uid).acknowledged))
    const [now, setNow] = useState(() => clock())
    const [placedIDs, setPlacedIDs] = useState<Set<string>>(new Set())
    const [panelOpen, setPanelOpen] = useState(false)
    const [pulseKey, setPulseKey] = useState<number | undefined>(undefined)
    const [announcement, setAnnouncement] = useState('')

    const loadedRef = useRef(false)
    const seenIDsRef = useRef<Set<string>>(new Set())
    const viewedRef = useRef<Set<string>>(new Set())
    const ackShownAtRef = useRef<{ key: string, at: number } | undefined>(undefined)

    // Subscriptions ----------------------------------------------------------
    useEffect(() => {
        loadedRef.current = false
        seenIDsRef.current = new Set()
        setPlacedIDs(new Set())
        setNotices([])
        const unsubscribe = service.subscribeToNotices(
            organizationID,
            (next) => {
                setNow(clock())
                setNotices(next)
            },
            (error) => console.error('[PlatformNotices] notices subscription failed', error)
        )
        return unsubscribe
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service, organizationID])

    useEffect(() => {
        const local = readLocalState(uid)
        setLocalDismissed(new Set(local.dismissed))
        setLocalAcknowledged(new Set(local.acknowledged))
        if (!uid) {
            setUserStates({})
            return
        }
        return service.subscribeToUserState(
            uid,
            setUserStates,
            (error) => console.error('[PlatformNotices] user state subscription failed', error)
        )
    }, [service, uid])

    // Re-evaluate at the next start / end / cue boundary without a reload.
    useEffect(() => {
        const boundary = nextBoundary(notices, now)
        if (boundary === undefined) {
            return
        }
        const timeout = window.setTimeout(() => setNow(clock()), Math.min(Math.max(boundary - now, 0) + 50, MAX_TIMEOUT_MS))
        return () => window.clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notices, now])

    // Derived sets -----------------------------------------------------------
    const visible = useMemo(
        () => sortNotices(notices.filter((notice) => isNoticeLive(notice, now) && !isDismissed(notice, userStates, localDismissed))),
        [notices, now, userStates, localDismissed]
    )

    // Deferral rule: a warning/communication that arrives while the bar already
    // shows a row goes to the counter until the next navigation.
    useEffect(() => {
        const newcomers = visible.filter((notice) => !seenIDsRef.current.has(notice.id))
        visible.forEach((notice) => seenIDsRef.current.add(notice.id))
        if (!loadedRef.current) {
            if (visible.length === 0 && notices.length === 0) {
                return
            }
            loadedRef.current = true
            setPlacedIDs(new Set(visible.map((notice) => notice.id)))
            return
        }
        const { rows } = selectBarRows(visible, placedIDs)
        if (rows.length === 0 && visible.length > 0) {
            // Nothing on screen: the bar may grow, so everything takes its place.
            setPlacedIDs(new Set(visible.map((notice) => notice.id)))
            return
        }
        const deferred = newcomers.filter((notice) => notice.level !== 'critical' && !placedIDs.has(notice.id))
        if (deferred.length > 0) {
            setPulseKey(clock())
            setAnnouncement(strings.newDeferred)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    useEffect(() => {
        if (!loadedRef.current) {
            return
        }
        setPlacedIDs(new Set(visible.map((notice) => notice.id)))
        setAnnouncement('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationKey])

    const { rows, hiddenCount } = useMemo(() => selectBarRows(visible, placedIDs), [visible, placedIDs])
    const pendingAck = useMemo(
        () => pendingAcknowledgements(visible, userStates, localAcknowledged),
        [visible, userStates, localAcknowledged]
    )

    // Display ----------------------------------------------------------------
    const scopeCue = useCallback((notice: PlatformNotice) => {
        if (notice.scope !== 'organization') {
            return undefined
        }
        return hasMultipleOrganizations && organizationName
            ? strings.scopeOrganizationNamed(organizationName)
            : strings.scopeOrganization
    }, [hasMultipleOrganizations, organizationName, strings])

    const toDisplay = useCallback((notice: PlatformNotice): PlatformNoticeDisplay => {
        const meta: string[] = []
        const scope = scopeCue(notice)
        if (scope) meta.push(scope)
        if (isEdited(notice) && now - notice.updatedAt < UPDATED_CUE_MS) meta.push(strings.updated)
        return {
            key: noticeKey(notice),
            notice,
            level: notice.level,
            title: resolveLocalizedContent(notice.title, language),
            body: resolveLocalizedContent(notice.body, language),
            meta,
            cta: notice.cta
                ? { label: resolveLocalizedContent(notice.cta.label, language), href: notice.cta.href }
                : undefined,
            canDismiss: notice.level !== 'critical',
        }
    }, [language, now, scopeCue, strings])

    const rowDisplays = useMemo(() => rows.map(toDisplay), [rows, toDisplay])
    const allDisplays = useMemo(() => visible.map(toDisplay), [visible, toDisplay])
    const ackDisplay = pendingAck.length > 0 ? toDisplay(pendingAck[0]) : undefined

    const ackSubtitle = ackDisplay
        ? [strings.levels.critical, scopeCue(ackDisplay.notice) ?? strings.scopeGeneral, formatPublishedAt(ackDisplay.notice.publishedAt, language)].join(' · ')
        : ''

    // Close the popover once nothing is left behind the pill.
    useEffect(() => {
        if (visible.length === 0) {
            setPanelOpen(false)
        }
    }, [visible.length])

    // Analytics --------------------------------------------------------------
    const payloadFor = (notice: PlatformNotice) => ({
        noticeID: notice.id,
        noticeVersion: notice.version,
        level: notice.level,
        scope: notice.scope,
    })

    useEffect(() => {
        const shown = ackDisplay ? [...rows, ackDisplay.notice] : rows
        for (const notice of shown) {
            const key = noticeKey(notice)
            if (!viewedRef.current.has(key)) {
                viewedRef.current.add(key)
                PlatformNoticesAnalyticsHelper.trackViewed(payloadFor(notice))
            }
        }
        if (ackDisplay && ackShownAtRef.current?.key !== ackDisplay.key) {
            ackShownAtRef.current = { key: ackDisplay.key, at: clock() }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, ackDisplay?.key])

    // Actions ----------------------------------------------------------------
    const dismiss = useCallback((notice: PlatformNotice) => {
        if (notice.level === 'critical') {
            return
        }
        const key = noticeKey(notice)
        setLocalDismissed((current) => new Set(current).add(key))
        PlatformNoticesAnalyticsHelper.trackDismissed(payloadFor(notice))
        if (!uid) {
            return
        }
        service.markDismissed(uid, notice).catch((error) => {
            console.error('[PlatformNotices] dismissal not saved remotely', error)
            persistLocalState(uid, 'dismissed', key)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service, uid])

    const acknowledge = useCallback(() => {
        if (!ackDisplay) {
            return
        }
        const notice = ackDisplay.notice
        const key = noticeKey(notice)
        const shownAt = ackShownAtRef.current?.key === key ? ackShownAtRef.current.at : clock()
        setLocalAcknowledged((current) => new Set(current).add(key))
        PlatformNoticesAnalyticsHelper.trackAcknowledged({ ...payloadFor(notice), msSinceShown: Math.max(0, clock() - shownAt) })
        if (!uid) {
            return
        }
        service.markAcknowledged(uid, notice).catch((error) => {
            console.error('[PlatformNotices] acknowledgement not saved remotely', error)
            persistLocalState(uid, 'acknowledged', key)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ackDisplay, service, uid])

    const linkClicked = useCallback((notice: PlatformNotice, href: string) => {
        PlatformNoticesAnalyticsHelper.trackLinkClicked({ ...payloadFor(notice), linkHost: linkHost(href) })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const togglePanel = useCallback(() => setPanelOpen((open) => !open), [])
    const closePanel = useCallback(() => setPanelOpen(false), [])

    return {
        strings,
        rows: rowDisplays,
        hiddenCount,
        allNotices: allDisplays,
        topNotice: allDisplays[0],
        panelOpen,
        togglePanel,
        closePanel,
        pulseKey,
        announcement,
        ackNotice: ackDisplay,
        ackSubtitle,
        acknowledge,
        dismiss,
        linkClicked,
    }
}

export type PlatformNoticesViewModel = ReturnType<typeof usePlatformNoticesViewModel>
